import axios from "axios";
import { apiEndpoints } from "./endpoints";
import { getToken } from "../constants/asyncStoreManager";
import { BASE_URL } from "../constants/configConstants";
import { string } from "../constants/string";
import { clearStates } from "../constants/globalFunction";
import { cachedRequest, clearApiCacheEntry } from "./requestCache";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const MAX_429_RETRIES = 3;

const makeApiRequest = async (
  endpoint,
  data = {},
  options = {},
) => {
  const {
    requiresAuth = false,
    contentType = "application/json",
    method = "POST",
  } = options;

  try {

    //add
    if (!endpoint) {
    console.error("API endpoint is missing:", endpoint);
    return {
      status: 0,
      message: "API endpoint is missing.",
    };
  }
  //
    let headers = {};

    if (requiresAuth) {
      const token = await getToken();
      headers.Authorization = `Bearer ${token}`;
    }

    if (contentType === "multipart/form-data") {
      headers["Content-Type"] = "multipart/form-data";
    } else if (contentType === "application/json") {
      headers["Content-Type"] = "application/json";
    }

    const config = {
      method,
      url: BASE_URL + endpoint,
      data: method === "GET" ? undefined : data,
      params: method === "GET" ? data : undefined,
      headers,
      timeout: 30000,
    };

    const response = await axios(config);
    return response.data;
  } catch (err) {
    if (err.response) {
      const status = err.response.status;
      if (status === 429) {
        console.warn(`429 Too Many Requests: ${endpoint}`);
      } else {
        console.log(`${endpoint} api fail.. :`, err.response);
      }

      // Handle authentication errors
      if (
        err.response?.data?.message === "Unauthenticated." &&
        err.response?.status === 401
      ) {
        clearStates();
      }

      return {
        status: err.response.status,
        message: err.response.data.message,
      };
    } else if (err.request) {
      return {
        status: 0,
        message: string.error.network_error,
      };
    } else {
      return {
        status: 0,
        message: string.error.failed_due_to_internal_error,
      };
    }
  }
};


export const appApiHandler = {
  register: async (reqObj) => {
    return makeApiRequest(apiEndpoints.REGISTER, reqObj, {
      contentType: "multipart/form-data",
    });
  },
  login: async (reqObj) => {
    return makeApiRequest(apiEndpoints.LOGIN, reqObj);
  },
  checkUser: async (reqObj) => {
    return makeApiRequest(apiEndpoints.CHECK_USER, reqObj);
  },
  socialLogin: async (reqObj) => {
    return makeApiRequest(apiEndpoints.SOCIAL_LOGIN, reqObj);
  },
  sendOtp: async (reqObj) => {
    return makeApiRequest(apiEndpoints.SEND_OTP, reqObj);
  },
  verifyOtp: async (reqObj) => {
    return makeApiRequest(apiEndpoints.VERIFY_OTP, reqObj);
  },
  resetPassword: async (reqObj) => {
    return makeApiRequest(apiEndpoints.RESET_PASSWORD, reqObj);
  },
  changePassword: async (reqObj) => {
    return makeApiRequest(apiEndpoints.CHANGE_PASSWORD, reqObj, {
      requiresAuth: true,
    });
  },
  getProfile: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_PROFILE, reqObj, {
      requiresAuth: true,
    });
  },
  updateProfile: async (reqObj) => {
    return makeApiRequest(apiEndpoints.UPDATE_PROFILE, reqObj, {
      requiresAuth: true,
      contentType: "multipart/form-data",
    });
  },
  getHomeData: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_HOME_DATA, reqObj);
  },
  getAllVideos: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_ALL_VIDEOS, reqObj);
  },

  getCategories: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_ALL_CATEGORIES, reqObj);
  },

  //Socia Media
  getSocialMedia: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_ALL_SOCIAL_MEDIA, reqObj)
  },

  //Testimonial
  getTestimonial: async (reqObj) => {
  return makeApiRequest(apiEndpoints.GET_TESTIMONIAL, reqObj);
  },

  getProducts: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_PRODUCTS, reqObj);
  },
  getProductDetails: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_PRODUCT_DETAILS, reqObj);
  },
  searchProducts: async (reqObj) => {
    return makeApiRequest(apiEndpoints.SEARCH_PRODUCTS, reqObj);
  },
  getProductFilterParams: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_PRODUCT_FILTER_PARAMS, reqObj);
  },
  filterProducts: async (reqObj) => {
    return makeApiRequest(apiEndpoints.FILTER_PRODUCTS, reqObj);
  },
  addToWishlist: async (reqObj) => {
    return makeApiRequest(apiEndpoints.ADD_TO_WISHLIST, reqObj, {
      requiresAuth: true,
    });
  },
  removeFromWishlist: async (reqObj) => {
    return makeApiRequest(apiEndpoints.REMOVE_FROM_WISHLIST, reqObj, {
      requiresAuth: true,
    });
  },

  getWishlist: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_WISHLIST, reqObj, {
      requiresAuth: true,
    });
  },

  addToCart: async (reqObj) => {

    return makeApiRequest(apiEndpoints.ADD_TO_CART, reqObj, {
      requiresAuth: true,
    });
  },
  
  removeFromCart: async (reqObj) => {
    return makeApiRequest(apiEndpoints.REMOVE_FROM_CART, reqObj, {
      requiresAuth: true,
    });
  },

  getCart: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_CART, reqObj, {
      requiresAuth: true,
    });
  },

  getCountries: async () => {
    return makeApiRequest(
      apiEndpoints.GET_COUNTRIES,
      {},
      {
        requiresAuth: true,
      },
    );
  },
  getStateCityDetails: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_STATE_CITY_DETAILS, reqObj, {
      requiresAuth: true,
    });
  },
  addAddress: async (reqObj) => {
    return makeApiRequest(apiEndpoints.ADD_ADDRESS, reqObj, {
      requiresAuth: true,
    });
  },
  updateAddress: async (reqObj) => {
    return makeApiRequest(apiEndpoints.UPDATE_ADDRESS, reqObj, {
      requiresAuth: true,
    });
  },
  deleteAddress: async (reqObj) => {
    return makeApiRequest(apiEndpoints.DELETE_ADDRESS, reqObj, {
      requiresAuth: true,
    });
  },
  getAddress: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_ADDRESS, reqObj, {
      requiresAuth: true,
    });
  },
  placeOrder: async (reqObj) => {
    return makeApiRequest(apiEndpoints.PLACE_ORDER, reqObj, {
      requiresAuth: true,
    });
  },
  getOrders: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_ORDERS, reqObj, {
      requiresAuth: true,
    });
  },
  getOrderDetails: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_ORDER_DETAILS, reqObj, {
      requiresAuth: true,
    });
  },
  addReviewRating: async (reqObj) => {
    return makeApiRequest(apiEndpoints.ADD_REVIEW_RATING, reqObj, {
      requiresAuth: true,
    });
  },
  raiseSupportTicket: async (reqObj) => {
    return makeApiRequest(apiEndpoints.RAISE_SUPPORT_TICKET, reqObj, {
      requiresAuth: true,
    });
  },
  updateSupportTicket: async (reqObj) => {
    return makeApiRequest(apiEndpoints.UPDATE_SUPPORT_TICKET, reqObj, {
      requiresAuth: true,
    });
  },
  getSupportTickets: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_SUPPORT_TICKETS, reqObj, {
      requiresAuth: true,
    });
  },
  getTicketChatHistory: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_SUPPORT_CHAT_HISTORY, reqObj, {
      requiresAuth: true,
    });
  },
  addMessageToTicket: async (reqObj) => {
    return makeApiRequest(apiEndpoints.ADD_MESSAGE_TO_TICKET, reqObj, {
      requiresAuth: true,
      contentType: "multipart/form-data",
    });
  },
  getNotifications: async (reqObj) => {
    return makeApiRequest(apiEndpoints.GET_NOTIFICATIONS, reqObj, {
      requiresAuth: true,
    });
  },
  logOut: async (reqObj) => {
    return makeApiRequest(apiEndpoints.LOGOUT, reqObj);
  },
  getAdminBankDetails: async () => {
    return makeApiRequest(apiEndpoints.GET_ADMIN_BANK_DETAILS);
  },
  createOrder: async (reqObj) => {
    return makeApiRequest(apiEndpoints.CREATE_ORDER, reqObj, {
      requiresAuth: true,
    });
  },
};
